/**
 * The page's places and the words about them: which place the address names, which one the page opens
 * on, and how the rooms' lines, files and news are grouped and worded. Pure, so every size draws the same.
 */
import { labelParts } from "../../../shared/live-time";
import type { GuestPlace, GuestTalkResponse, TalkFace, TalkFile, TalkMessage, TalkRoomKind } from "../../../shared/talk";
import { sepLabel } from "./model";

/** Where the person is on the page: `?at=` says it, `&q=` is a search's words. */
export type Place =
  | { kind: "new" }
  | { kind: "direct" }
  | { kind: "files" }
  | { kind: "search"; q: string }
  | { kind: "everyone"; id: string }
  | { kind: "announce"; id: string }
  | { kind: "circle"; id: string };

export function parsePlace(at: string | null, q: string | null): Place | null {
  if (at === "new" || at === "direct" || at === "files") return { kind: at };
  if (at === "search") return { kind: "search", q: q ?? "" };
  const m = /^(everyone|announce|circle):(.+)$/.exec(at ?? "");
  return m ? { kind: m[1] as "everyone" | "announce" | "circle", id: m[2] } : null;
}

/** The address's own words for a place, for the links the page and the server's emails share. */
export function placeParams(p: Place): Record<string, string> {
  if (p.kind === "everyone" || p.kind === "announce" || p.kind === "circle") return { at: `${p.kind}:${p.id}` };
  if (p.kind === "search") return p.q ? { at: "search", q: p.q } : { at: "search" };
  return { at: p.kind };
}

export const samePlace = (a: Place, b: Place) => JSON.stringify(placeParams(a)) === JSON.stringify(placeParams(b));

/** The key `seen` takes for a place, or null for a place that has nothing to mark. */
export function seenKey(p: Place): GuestPlace | null {
  if (p.kind === "direct") return "direct";
  if (p.kind === "everyone") return `everyone:${p.id}`;
  if (p.kind === "announce") return `announce:${p.id}`;
  if (p.kind === "circle") return `circle:${p.id}`;
  return null;
}

/** Whether something is new. A call that is on shows beside the places while What's new is closed, so it does not make news. */
export const hasNews = (t: GuestTalkResponse) => t.news.length > 0;

/** Whether the person has this place: What's new only while something is new, rooms and circles only theirs. */
export function placeExists(p: Place, t: GuestTalkResponse): boolean {
  if (p.kind === "new") return hasNews(t);
  if (p.kind === "everyone") return t.rooms.some((r) => r.webinarId === p.id && r.everyoneChat);
  if (p.kind === "announce") return t.rooms.some((r) => r.webinarId === p.id);
  if (p.kind === "circle") return t.rooms.some((r) => r.circles.some((c) => c.circleId === p.id));
  return true;
}

/** The page opens on What's new when there is news, otherwise on the host. */
export const openingPlace = (t: GuestTalkResponse): Place => (hasNews(t) ? { kind: "new" } : { kind: "direct" });

// --- words ---

/** "9:58 AM". */
export function timeOf(at: number): string {
  return labelParts(new Date(at * 1000).toISOString(), undefined, "short")?.time ?? "";
}

/** "Since you were here on Thursday". */
export function sinceWords(lastHereAt: number | null, nowSec: number): string {
  if (lastHereAt === null) return "Since you were last here";
  const { day } = sepLabel(lastHereAt, nowSec);
  if (day === "Today") return "Since you were here earlier today";
  if (day === "Yesterday") return "Since you were here yesterday";
  return `Since you were here on ${day}`;
}

/** "Thursday", "Today", "Fri, Oct 9": how a day is named beside a time. */
export const dayOf = (at: number, nowSec: number): string => sepLabel(at, nowSec).day;

/** "Mon 19 Oct", for a call or a replay. */
export function shortDate(at: number): string {
  const p = labelParts(new Date(at * 1000).toISOString(), undefined, "short");
  return p ? `${p.weekday} ${p.day} ${p.month}` : "";
}

/** "Mon 19 Oct · 26 min". */
export const callWords = (at: number, minutes: number) => `${shortDate(at)} · ${minutes} min`;

/** "Aisha, Priya and 3 others". */
export function namesOf(names: readonly string[], total = names.length): string {
  const first = names.map((n) => n.split(" ")[0]);
  if (total <= 1) return first[0] ?? "";
  if (total === 2 || first.length < 2) return first.slice(0, 2).join(" and ");
  const rest = total - 2;
  return `${first[0]}, ${first[1]} and ${rest} ${rest === 1 ? "other" : "others"}`;
}

/** "Aisha, Priya and 3 others", from the faces of a call. */
/** "3 are in": how many are in a call now, said only when someone is. */
export const joinedWords = (count: number) => (count > 0 ? `${count.toLocaleString("en-US")} ${count === 1 ? "is" : "are"} in` : "");

export const inWordsOf = (faces: readonly TalkFace[]) => namesOf(faces.map((f) => f.name), faces.length);

/** How long a call has been on, as "4 min" or "just now". */
export function liveFor(wentLiveAt: number, nowMs: number): string {
  const min = Math.max(0, Math.floor((nowMs - wentLiveAt * 1000) / 60_000));
  return min < 1 ? "just now" : `${min} min`;
}

const startOfDay = (ms: number) => new Date(new Date(ms).getFullYear(), new Date(ms).getMonth(), new Date(ms).getDate()).getTime();

/** "today" or "tomorrow" for a time ahead, else null. */
export function relDay(at: number, nowMs: number): "today" | "tomorrow" | null {
  const days = Math.round((startOfDay(at * 1000) - startOfDay(nowMs)) / 86_400_000);
  return days === 0 ? "today" : days === 1 ? "tomorrow" : null;
}

/** "today, 6:00 PM", "tomorrow, 6:00 PM", "Tue 27 Oct, 6:00 PM": when the next live is, for a phone's strip. */
export function liveWhen(at: number, nowMs: number): string {
  return `${relDay(at, nowMs) ?? shortDate(at)}, ${timeOf(at)}`;
}

/** What the composer's place says when posting is off: a pause with an end says when, in the person's own time. */
export function offWordsOf(d: { postNote: string | null; pausedUntil: number | null } | null | undefined, nowMs: number): string {
  if (!d) return "";
  if (d.pausedUntil === null) return d.postNote ?? "";
  const at = d.pausedUntil;
  return `Posting is paused until ${relDay(at, nowMs) ?? shortDate(at)} at ${timeOf(at)}. You can still read everything.`;
}

/** What a room with no live to show is called in its sessions: the server's kind, "Recorded talk" for one that plays any time. */
export const kindWord = (kind: TalkRoomKind | undefined): string => (kind === "Weekly" || kind === "One-time" ? kind : "Recorded talk");

/** "27 of 31 voted". */
export const votedWords = (voted: number, of: number) => `${voted} of ${of} voted`;

/** "2 min" and "1 hour 5 min" from minutes. */
export function minutesWords(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h} ${h === 1 ? "hour" : "hours"}${m ? ` ${m} min` : ""}`;
}

/** The first word of a person's name, for "You" and "Maya". */
export const firstName = (name: string) => name.split(" ")[0] ?? name;

// --- a room's or circle's lines ---

export type ChatLine =
  | { type: "sep"; key: string; day: string }
  | { type: "new"; key: string }
  | { type: "msg"; key: string; message: TalkMessage; mine: boolean };

const DAY = 86_400;

/**
 * The lines of a place in order, with a day above each new day and "New since you were here" above the
 * first line from someone else that came after `seenAt`.
 */
export function chatLines(messages: readonly TalkMessage[], seenAt: number | null, nowSec: number): ChatLine[] {
  const out: ChatLine[] = [];
  let day = "";
  let marked = seenAt === null;
  for (const m of messages) {
    const d = dayOf(m.createdAt, nowSec);
    const dayKey = Math.floor(m.createdAt / DAY);
    if (String(dayKey) !== day) {
      day = String(dayKey);
      out.push({ type: "sep", key: `sep-${m.id}`, day: d });
    }
    const mine = m.who.kind === "member" && m.who.me === true;
    if (!marked && !mine && seenAt !== null && m.createdAt > seenAt) {
      out.push({ type: "new", key: "new" });
      marked = true;
    }
    out.push({ type: "msg", key: m.clientId ?? m.id, message: m, mine });
  }
  return out;
}

// --- Files ---

export type FileFilter = "all" | "maya" | "room" | "you";

export const fileFrom = (f: TalkFile): FileFilter => (f.by.kind === "host" ? "maya" : f.by.me ? "you" : "room");

export function filterFiles(files: readonly TalkFile[], filter: FileFilter): TalkFile[] {
  return filter === "all" ? [...files] : files.filter((f) => fileFrom(f) === filter);
}

export function weekOf(at: number, nowSec: number): "This week" | "Last week" | "Earlier" {
  const days = (nowSec - at) / DAY;
  return days < 7 ? "This week" : days < 14 ? "Last week" : "Earlier";
}

/** "Maya", "You", or a member's first name, for the line under a file. */
export const fileBy = (f: TalkFile) => (f.by.kind === "member" && f.by.me ? "You" : firstName(f.by.name));
